/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/require-default-props */
import { Box, IconButton } from '@mui/material';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LocationPrecision } from '../../../../api/cep/types';
import {
  IconFullscreenExitMS,
  IconFullscreenMS,
} from '../../../../constants/icons';

interface CustomMapProps {
  searchMap?: boolean;
  address?: string;
  district?: string;
  city?: string;
  uf?: string;
  pinPlace: google.maps.LatLngLiteral;
  setPinPlace?: (place: google.maps.LatLngLiteral) => void;
  onSearchPrecision?: (precision: LocationPrecision) => void;
  onSearchError?: () => void;
  draggable: boolean;
}

export default function CustomMap({
  searchMap,
  address,
  district,
  city,
  uf,
  pinPlace,
  setPinPlace,
  onSearchPrecision,
  onSearchError,
  draggable,
}: CustomMapProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const center = useMemo(() => pinPlace, [pinPlace]);
  const map = useMap();

  const handleDragEnd = useCallback(
    (event: google.maps.MapMouseEvent): void => {
      if (map && setPinPlace && event.latLng) {
        setPinPlace({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      }
    },
    [map, setPinPlace]
  );

  useEffect(() => {
    if (!map || !address || !setPinPlace) return;

    const geocoder = new google.maps.Geocoder();
    const tryGeocode = (
      addr: string,
      restrictions: google.maps.GeocoderComponentRestrictions,
      level: number
    ): void => {
      geocoder.geocode(
        { address: addr, componentRestrictions: restrictions },
        (results, status) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const loc = results[0].geometry.location;
            const coord = { lat: loc.lat(), lng: loc.lng() };
            map.panTo(coord);
            setPinPlace(coord);

            if (onSearchPrecision) {
              const exactTypes = new Set(['ROOFTOP', 'RANGE_INTERPOLATED']);
              const isExactType = exactTypes.has(
                results[0].geometry.location_type
              );
              onSearchPrecision(
                isExactType && !results[0].partial_match
                  ? 'exact'
                  : 'approximate'
              );
            }
          } else if (level === 0 && district) {
            tryGeocode(
              district,
              {
                country: 'br',
                administrativeArea: uf,
                locality: city,
              },
              1
            );
          } else if (level === 1 && city) {
            tryGeocode(city, { country: 'br', administrativeArea: uf }, 2);
          } else if (onSearchError) {
            onSearchError();
          }
        }
      );
    };

    tryGeocode(
      [address, district].filter(Boolean).join(', '),
      { country: 'br', administrativeArea: uf, locality: city },
      0
    );
  }, [searchMap]);

  useEffect(() => {
    if (map) {
      map.panTo(pinPlace);
    }
  }, [map, pinPlace]);

  return (
    <Box className={`map-container ${isExpanded ? 'expanded' : ''}`}>
      <Map
        mapId="93ba1580047addb8"
        defaultZoom={16}
        defaultCenter={center}
        streetViewControl={false}
        mapTypeId="satellite"
        className="customMap"
        fullscreenControl={false}
        minZoom={4}
      >
        <AdvancedMarker
          position={pinPlace}
          draggable={draggable}
          onDragEnd={handleDragEnd}
        />
      </Map>
      <IconButton
        onClick={() => setIsExpanded(!isExpanded)}
        className="expand-button"
        aria-label={isExpanded ? 'Reduzir mapa' : 'Expandir mapa'}
        sx={{ borderRadius: '5%' }}
      >
        {isExpanded ? IconFullscreenExitMS : IconFullscreenMS}
      </IconButton>
    </Box>
  );
}
